// API Configuration - Single source of truth for all API settings
export const API_CONFIG = {
  // Base URL for all API calls
  BASE_URL: process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://apibeanbuzz.suprun.ca' : 'http://localhost:8086'),
  
  // API version
  VERSION: 'v1',
  
  // Timeout settings
  TIMEOUT: 30000, // 30 seconds
  
  // Rate limiting
  MAX_REQUESTS_PER_MINUTE: 50,
  RESET_INTERVAL: 60 * 1000, // 1 minute
  
  // Endpoints
  ENDPOINTS: {
    // Organization endpoints
    ORGS: {
      USER_ORGS: '/api/v1/orgs/system/user',
      CHECK_MEMBERSHIP: '/api/v1/orgs/system/check-membership',
      DEBUG_AUTH: '/api/v1/orgs/system/debug/auth',
      CREATE: '/api/v1/orgs',
      GET_BY_URL: (urlName: string) => `/api/v1/orgs/${urlName}`,
      GET_PUBLIC: (urlName: string) => `/api/v1/orgs/public/${urlName}`,
      UPDATE: (urlName: string) => `/api/v1/orgs/${urlName}`,
      DELETE: (urlName: string) => `/api/v1/orgs/${urlName}`,
      URL_AVAILABILITY: (urlName: string) => `/api/v1/orgs/url-availability/${urlName}`,
      MEMBERS: (urlName: string) => `/api/v1/orgs/${urlName}/members`,
      REMOVE_MEMBER: (urlName: string, email: string) => `/api/v1/orgs/${urlName}/members/${encodeURIComponent(email)}`,
      SETTINGS: (urlName: string) => `/api/v1/orgs/${urlName}/settings`,
    },
    
    // Menu endpoints
    MENU: {
      ITEMS: (urlName: string) => `/api/v1/menu/items/${urlName}`,
      PUBLIC_MENU: (urlName: string) => `/api/v1/menu/public/${urlName}`,
      CATEGORIES: (urlName: string) => `/api/v1/menu/categories/${urlName}`,
      PUBLIC_CATEGORIES: (urlName: string) => `/api/v1/menu/public/categories/${urlName}`,
      CREATE_ITEM: (urlName: string) => `/api/v1/menu/items/${urlName}`,
      UPDATE_ITEM: (urlName: string, itemId: number) => `/api/v1/menu/items/${urlName}/${itemId}`,
      DELETE_ITEM: (urlName: string, itemId: number) => `/api/v1/menu/items/${urlName}/${itemId}`,
      CREATE_CATEGORY: (urlName: string) => `/api/v1/menu/categories/${urlName}`,
      UPDATE_CATEGORY: (urlName: string, categoryId: number) => `/api/v1/menu/categories/${urlName}/${categoryId}`,
      DELETE_CATEGORY: (urlName: string, categoryId: number) => `/api/v1/menu/categories/${urlName}/${categoryId}`,
    },
    
    // Orders endpoints
    ORDERS: {
      GET_ALL: (urlName: string) => `/api/v1/orgs/${urlName}/orders`,
      GET_ACTIVE: (urlName: string) => `/api/v1/orgs/${urlName}/orders/active`,
      CREATE: (urlName: string) => `/api/v1/orgs/${urlName}/orders`,
      GET_BY_ID: (urlName: string, orderId: string) => `/api/v1/orgs/${urlName}/orders/${orderId}`,
      UPDATE_STATUS: (urlName: string, orderId: string) => `/api/v1/orgs/${urlName}/orders/${orderId}/status`,
      DELETE: (urlName: string, orderId: string) => `/api/v1/orgs/${urlName}/orders/${orderId}`,
      PUBLIC_CREATE: (orgUrl: string, tableId: string) => `/api/v1/orgs/${orgUrl}/orders/public/${orgUrl}/${tableId}`,
      PUBLIC_STATUS: (orgUrl: string, orderId: string) => `/api/v1/orgs/${orgUrl}/orders/public/${orgUrl}/${orderId}`,
    },
    
    // Tables endpoints
    TABLES: {
      GET_ALL: (urlName: string) => `/api/v1/tables/${urlName}`,
      GET_ACTIVE: (urlName: string) => `/api/v1/tables/${urlName}/active`,
      CREATE: (urlName: string) => `/api/v1/tables/${urlName}`,
      UPDATE: (urlName: string, tableId: string) => `/api/v1/tables/${urlName}/${tableId}`,
      DELETE: (urlName: string, tableId: string) => `/api/v1/tables/${urlName}/${tableId}`,
      PUBLIC_ACTIVE: (urlName: string) => `/api/v1/tables/public/${urlName}`,
      PUBLIC_TABLE: (urlName: string, tableId: string) => `/api/v1/tables/public/${urlName}/table/${tableId}`,
    },
  },
  
  // Headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
  
  // Status codes
  STATUS_CODES: {
    OK: 200,
    CREATED: 201,
    NO_CONTENT: 204,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    RATE_LIMITED: 429,
    INTERNAL_SERVER_ERROR: 500,
  },
} as const;

// Helper function to build full API URL
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to get environment-specific API URL
export const getApiUrl = (): string => {
  return API_CONFIG.BASE_URL;
};

// Helper function to check if we're in development
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

// Helper function to check if we're in production
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
}; 